import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Fuel, Gauge, Clock, Activity, RefreshCw, Loader2, AlertTriangle, Ship, Plus, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';

import fuelApi from '@/services/fuelApi';
import operationsApi from '@/services/operationsApi';
import { useAuth } from '@/contexts/AuthContext';
import RefuelDialog from '@/components/fuel/RefuelDialog';
import AdjustSaldoDialog from '@/components/fuel/AdjustSaldoDialog';
import FuelTimeline from '@/components/fuel/FuelTimeline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useTimezone } from '@/contexts/TimezoneContext';

// ── Formatação ────────────────────────────────────────────────────────────────
// Volumes em litros chegam prontos do backend (`*_l`, ADR-2026-10-25).
const fmtLnum = (l) =>
  l == null || Number.isNaN(l) ? '—' : `${Math.round(l).toLocaleString('pt-BR')} L`;
const fmtNum = (v, dec = 1) =>
  v == null || Number.isNaN(v) ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: dec });
const fmtPct = (pct) =>
  pct == null ? '—' : `±${Number(pct).toLocaleString('pt-BR', { maximumFractionDigits: 1, useGrouping: false })}%`;

// Banda de cor do % de defasagem (espelha o lab): <10 verde, ≤50 âmbar, >50 vermelho.
const pctColor = (pct) => {
  if (pct == null || !Number.isFinite(pct)) return 'text-muted-foreground';
  if (pct < 10) return 'text-emerald-500';
  if (pct <= 50) return 'text-amber-500';
  return 'text-red-500';
};

// Semáforo de reserva → badge.
const RESERVE_BADGE = {
  ok: { label: 'OK', cls: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent' },
  baixo: { label: 'Baixo', cls: 'bg-amber-500 hover:bg-amber-500 text-black border-transparent' },
  critico: { label: 'Crítico', cls: 'bg-red-600 hover:bg-red-600 text-white border-transparent' },
  unknown: { label: '—', cls: 'bg-muted text-muted-foreground border-transparent' },
};

// Rótulo de motor a partir do path canônico (genérico p/ a frota).
const MOTOR_LABEL = { port: 'Bombordo', starboard: 'Boreste', center: 'Central', main: 'Principal' };
const motorLabel = (path) => {
  const k = String(path).replace('propulsion.', '').replace('.fuel.rate', '');
  return MOTOR_LABEL[k] || k;
};

const ENTRY_MODE_LABEL = { manual: 'Manual', nfe: 'NF-e' };

// Origem da detecção (D-11): worker server-side (Plan 06) grava detected_by;
// o lab (client-side) NÃO grava esse campo — ausência = "detector do lab".
const DETECTED_BY_LABEL = { server_detector: 'detector do servidor' };
const detectedByLabel = (v) => DETECTED_BY_LABEL[v] || 'detector do lab';

// Copy única de 1º uso (UI-SPEC S3/3a, Contradição 6) — substitui a orientação antiga
// para não coexistirem duas mensagens divergentes no mesmo caso `no_baseline`.
const ERROR_MSG = {
  no_baseline: 'Registre um ajuste de saldo inicial para começar a controlar o combustível deste equipamento.',
  influx_unavailable: 'Telemetria de consumo indisponível no momento (InfluxDB). O saldo não pôde ser computado.',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── KPI card ────────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <div className={`text-lg font-semibold ${accent || 'text-foreground'}`}>{value}</div>
          {sub ? <p className="text-xs text-muted-foreground mt-0.5">{sub}</p> : null}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function FuelControlPage() {
  const { formatDateTime } = useTimezone();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();

  // equipments já vem GATEADO por uses_fuel (só tipos com o flag ligado entram aqui —
  // AMENDA 1/2, D-18: paridade LAB para a frota sem hardcode de nome de equipamento).
  const [equipments, setEquipments] = useState([]);
  const [equipmentsLoaded, setEquipmentsLoaded] = useState(false);
  // Deep-link (backlog 3.1): /operations/fuel-control?equipment=<id>&action=refuel
  // pré-seleciona a embarcação e abre o dialog de lançamento (banner da TripsPage).
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(() => searchParams.get('equipment') || '');
  const [saldo, setSaldo] = useState(null);
  const [defasagem, setDefasagem] = useState(null);
  const [records, setRecords] = useState([]);
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refuelOpen, setRefuelOpen] = useState(() => searchParams.get('action') === 'refuel');
  const [adjustOpen, setAdjustOpen] = useState(false);
  // Conciliação de detecção (D-11): qual detecção está sendo conciliada no
  // RefuelDialog (null = lançamento manual, comportamento de sempre).
  const [reconcileDetection, setReconcileDetection] = useState(null);
  // Descarte de detecção (D-11): confirmação leve com motivo opcional.
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissing, setDismissing] = useState(false);

  const loadEquipments = useCallback(async () => {
    try {
      const [eqRes, typesRes] = await Promise.all([
        operationsApi.getEquipments(),
        axios.get(`${API}/equipment-types`),
      ]);
      const allEquipments = eqRes.data || [];
      // Gate por TIPO (D-18): só entram equipamentos cujo tipo tem uses_fuel === true.
      const fuelTypeIds = new Set(
        (typesRes.data || []).filter((t) => t.uses_fuel === true).map((t) => t.id),
      );
      const gated = allEquipments.filter((e) => fuelTypeIds.has(e.equipment_type_id));
      setEquipments(gated);
      setSelectedEquipmentId((prev) => prev || (gated[0]?.id ?? ''));
    } catch (e) {
      console.error('[FuelControl] erro ao carregar embarcações:', e);
      toast.error('Erro ao carregar a lista de embarcações.');
    } finally {
      setEquipmentsLoaded(true);
    }
  }, []);

  const loadFuel = useCallback(async (equipmentId) => {
    if (!equipmentId) return;
    setLoading(true);
    // Reset: não exibir dado da embarcação anterior enquanto carrega.
    setSaldo(null);
    setDefasagem(null);
    setRecords([]);
    setDetections([]);
    // allSettled: a falha de um endpoint NÃO descarta os dados dos outros
    // (um 500 transitório na defasagem não pode apagar um saldo que veio OK).
    const [saldoR, defR, recR, detR] = await Promise.allSettled([
      fuelApi.getFuelSaldo(equipmentId),
      fuelApi.getFuelDefasagem(equipmentId),
      fuelApi.getFuelRecords(equipmentId, { limit: 20 }),
      fuelApi.getDetections(equipmentId),
    ]);
    const fails = [];
    if (saldoR.status === 'fulfilled') setSaldo(saldoR.value.data || null);
    else fails.push('saldo');
    if (defR.status === 'fulfilled') setDefasagem(defR.value.data || null);
    else fails.push('defasagem');
    if (recR.status === 'fulfilled') setRecords(recR.value.data?.records || []);
    else fails.push('histórico');
    if (detR.status === 'fulfilled') setDetections(detR.value.data?.detections || []);
    else fails.push('detecções');
    if (fails.length) {
      console.error('[FuelControl] falha ao carregar:', fails);
      toast.error(`Erro ao carregar: ${fails.join(', ')}.`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  useEffect(() => {
    loadFuel(selectedEquipmentId);
  }, [selectedEquipmentId, loadFuel]);

  const saldoError = saldo?.error || null;
  const reserve = RESERVE_BADGE[saldo?.reserve_status] || RESERVE_BADGE.unknown;
  const byMotorEntries = useMemo(
    () => Object.entries(saldo?.by_motor || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0)),
    [saldo],
  );
  const defSummary = defasagem?.summary || {};
  const defRows = defasagem?.rows || [];
  const selectedEquipment = equipments.find((e) => e.id === selectedEquipmentId);
  const selectedEquipmentLabel = selectedEquipment
    ? `${selectedEquipment.name}${selectedEquipment.tag ? ` (${selectedEquipment.tag})` : ''}`
    : null;

  // Conciliação (D-11): abre o RefuelDialog pré-preenchido com a detecção.
  const openReconcile = (detection) => {
    setReconcileDetection(detection);
    setRefuelOpen(true);
  };
  // Subida do sensor calculada no backend (rise_m3 / rise_l) — a tela não
  // recalcula telemetria (INV-UI-TRAIL-001, ADR-2026-10-25).
  const reconcileSensorReadingM3 = reconcileDetection?.rise_m3 ?? null;
  const reconcileSensorReadingL = reconcileDetection?.rise_l ?? null;

  // Descarte (D-11): confirmação leve, append-only — NÃO apaga nada, só registra
  // detection_dismissed (motivo opcional).
  async function handleDismissConfirm() {
    if (!dismissTarget || dismissing) return;
    setDismissing(true);
    try {
      await fuelApi.dismissDetection({
        equipment_id: selectedEquipmentId,
        event_ref: dismissTarget.id,
        reason: dismissReason.trim() || null,
      });
      toast.success('Detecção descartada.');
      setDismissTarget(null);
      setDismissReason('');
      loadFuel(selectedEquipmentId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Falha ao descartar a detecção.');
    } finally {
      setDismissing(false);
    }
  }

  return (
    <div className="page-container space-y-6" data-testid="fuel-control-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="pl-12 md:pl-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Fuel className="w-6 h-6 text-primary" />
            Controle de Combustível
          </h1>
          <p className="text-sm text-muted-foreground">
            Saldo, autonomia, reserva e defasagem por embarcação (server-authoritative)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => { setReconcileDetection(null); setRefuelOpen(true); }}
            disabled={!selectedEquipmentId}
            className="gap-2"
            data-testid="fuel-refuel-open"
          >
            <Plus className="w-4 h-4" />
            Lançar abastecimento
          </Button>
          <Button
            variant="outline"
            onClick={() => loadFuel(selectedEquipmentId)}
            disabled={loading || !selectedEquipmentId}
            className="gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Atualizar
          </Button>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="fuel-admin-menu">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!selectedEquipmentId}
                  onSelect={() => setAdjustOpen(true)}
                >
                  Ajustar saldo…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Gate por uses_fuel (D-18, AMENDA 2): nenhum tipo com a flag → estado vazio
          honesto no lugar do seletor confuso, sem parecer bug. */}
      {equipmentsLoaded && equipments.length === 0 ? (
        <Card data-testid="fuel-empty-gate">
          <CardContent className="p-8 flex flex-col items-center text-center gap-2">
            <Fuel className="w-8 h-8 text-muted-foreground opacity-50" />
            <p className="text-sm font-medium">Nenhum tipo de equipamento usa combustível.</p>
            <p className="text-xs text-muted-foreground max-w-md">
              Ative &quot;Usa combustível&quot; no tipo desejado em Cadastros → Tipos de Equipamento.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Seletor de embarcação */}
          <Card>
            <CardContent className="p-4">
              <div className="max-w-md space-y-2">
                <Label className="flex items-center gap-2">
                  <Ship className="w-4 h-4 text-muted-foreground" />
                  Embarcação
                </Label>
                <Select value={selectedEquipmentId} onValueChange={setSelectedEquipmentId}>
                  <SelectTrigger data-testid="fuel-control-equipment">
                    <SelectValue placeholder="Selecione uma embarcação" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipments.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.tag ? `(${e.tag})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Detecções pendentes (D-11): view server-side sobre o ledger — só
              renderiza quando há pendências (sem ruído quando vazia). */}
          {detections.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Detecções pendentes</CardTitle>
                  <Badge
                    className="bg-amber-500 hover:bg-amber-500 text-black border-transparent"
                    data-testid="fuel-detections-badge"
                  >
                    {detections.length}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Abastecimentos detectados pelo sensor aguardando conciliação. Concilie
                  para virar lançamento oficial ou descarte.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {detections.map((d) => (
                  <div
                    key={d.id}
                    className="border rounded-lg p-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">{formatDateTime(d.detected_at)}</p>
                      <p className="text-lg font-semibold">
                        {d.rise_l != null ? `+ ${fmtLnum(d.rise_l)}` : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">{detectedByLabel(d.detected_by)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => openReconcile(d)}
                        data-testid={`detection-reconcile-${d.id}`}
                      >
                        Conciliar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setDismissTarget(d); setDismissReason(''); }}
                        data-testid={`detection-dismiss-${d.id}`}
                      >
                        Descartar
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Saldo: KPIs + consumo por motor (ou aviso quando não há baseline/Influx).
              A defasagem e o histórico abaixo NÃO dependem do saldo e seguem visíveis. */}
          {saldoError ? (
            <Card className="border-amber-500/40">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-muted-foreground">{ERROR_MSG[saldoError] || `Saldo indisponível (${saldoError}).`}</p>
                  {saldoError === 'no_baseline' && (
                    isAdmin ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAdjustOpen(true)}
                        data-testid="fuel-first-use-adjust"
                      >
                        Registrar saldo inicial
                      </Button>
                    ) : (
                      <p className="text-xs text-muted-foreground">Peça a um administrador para registrar o saldo inicial.</p>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Fuel}
              label="Saldo atual"
              value={fmtLnum(saldo?.saldo_l)}
              sub={saldo?.saldo_m3 != null ? `${fmtNum(saldo.saldo_m3, 2)} m³` : null}
            />
            <KpiCard
              icon={Clock}
              label="Autonomia estimada"
              value={saldo?.autonomy_hours != null ? `~${fmtNum(saldo.autonomy_hours, 1)} h` : '—'}
              sub={saldo?.recent_rate_lh != null ? `a ${fmtNum(saldo.recent_rate_lh, 1)} L/h` : 'sem consumo recente'}
            />
            <KpiCard
              icon={Activity}
              label="Consumo recente"
              value={saldo?.recent_rate_lh != null ? `${fmtNum(saldo.recent_rate_lh, 1)} L/h` : '—'}
              sub="média das últimas 2 h"
            />
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Gauge className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Reserva</p>
                    <div className="mt-1">
                      <Badge className={reserve.cls}>{reserve.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      limite {fmtLnum(saldo?.reserve_threshold_l)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Consumo por motor (desde o último abastecimento)</CardTitle>
            </CardHeader>
            <CardContent>
              {byMotorEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem dados de consumo por motor no período.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Motor</TableHead>
                      <TableHead className="text-right">Consumo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byMotorEntries.map(([path, liters]) => (
                      <TableRow key={path}>
                        <TableCell>{motorLabel(path)}</TableCell>
                        <TableCell className="text-right">{fmtLnum(liters)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold">
                      <TableCell>Total consumido</TableCell>
                      <TableCell className="text-right">{fmtLnum(saldo?.consumed_l)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Histórico do tanque (D-15/D-16/D-17): saldo(t) computado no servidor +
          eventos + sensor (quando houver) + reserva. Independe do saldo atual
          (mostra a série mesmo se o saldo instantâneo estiver com erro). */}
      <FuelTimeline equipmentId={selectedEquipmentId} />

      {/* Defasagem (oficial × sensor × NF) — independe do saldo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Defasagem (oficial × sensor × NF)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span className="text-muted-foreground">Abastecimentos: <b className="text-foreground">{defSummary.total_refuels ?? '—'}</b></span>
            <span className="text-muted-foreground">Com sensor: <b className="text-foreground">{defSummary.with_sensor ?? '—'}</b></span>
            <span className="text-muted-foreground">Com NF: <b className="text-foreground">{defSummary.with_nf ?? '—'}</b></span>
            <span className="text-muted-foreground">Para revisão: <b className={defSummary.flagged ? 'text-red-500' : 'text-emerald-500'}>{defSummary.flagged ?? '—'}</b></span>
            <span className="text-muted-foreground">Defasagem méd.: <b className={pctColor(defSummary.avg_defasagem_pct)}>{fmtPct(defSummary.avg_defasagem_pct)}</b></span>
            <span className="text-muted-foreground">Defasagem máx.: <b className={pctColor(defSummary.max_defasagem_pct)}>{fmtPct(defSummary.max_defasagem_pct)}</b></span>
          </div>
          <p className="text-xs text-muted-foreground">
            Acima de {defasagem?.threshold_pct ?? 50}% destaca para revisão (não bloqueia lançamentos).
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Modo</TableHead>
                <TableHead className="text-right">Oficial</TableHead>
                <TableHead className="text-right">Sensor</TableHead>
                <TableHead className="text-right">Δ sensor</TableHead>
                <TableHead className="text-right">NF</TableHead>
                <TableHead className="text-right">Δ NF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {defRows.map((r, i) => (
                <TableRow key={r.id || i} className={r.flagged ? 'bg-red-500/5' : ''}>
                  <TableCell>{formatDateTime(r.timestamp)}</TableCell>
                  <TableCell className="text-muted-foreground">{ENTRY_MODE_LABEL[r.entry_mode] || '—'}</TableCell>
                  <TableCell className="text-right font-medium">{fmtLnum(r.official_l)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtLnum(r.sensor_l)}</TableCell>
                  <TableCell className={`text-right ${pctColor(r.sensor_defasagem_pct)}`}>{fmtPct(r.sensor_defasagem_pct)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtLnum(r.nf_l)}</TableCell>
                  <TableCell className={`text-right ${pctColor(r.nf_defasagem_pct)}`}>{fmtPct(r.nf_defasagem_pct)}</TableCell>
                </TableRow>
              ))}
              {defRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-6">Sem abastecimentos registrados.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Histórico de lançamentos — independe do saldo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Histórico de lançamentos</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Volume</TableHead>
                <TableHead className="text-right">Saldo após</TableHead>
                <TableHead>Origem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r, i) => (
                <TableRow key={r.id || i}>
                  <TableCell>{formatDateTime(r.timestamp)}</TableCell>
                  <TableCell className="text-muted-foreground">{r.type}</TableCell>
                  <TableCell className="text-right">{fmtLnum(r.volume_l)}</TableCell>
                  <TableCell className="text-right">{fmtLnum(r.saldo_l_after)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{r.saldo_source || r.entry_mode || '—'}</TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">Sem lançamentos.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </>
      )}

      {/* Escrita (backlog 3.1 — MVP): lançamento + ajuste administrativo.
          Conciliação (D-11): reconcileDetection != null pré-preenche o dialog. */}
      <RefuelDialog
        open={refuelOpen}
        onOpenChange={(v) => { setRefuelOpen(v); if (!v) setReconcileDetection(null); }}
        equipmentId={selectedEquipmentId}
        equipmentLabel={selectedEquipmentLabel}
        onSuccess={() => loadFuel(selectedEquipmentId)}
        eventRef={reconcileDetection?.id || null}
        sensorReadingM3={reconcileSensorReadingM3}
        sensorReadingL={reconcileSensorReadingL}
      />
      <AdjustSaldoDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        equipmentId={selectedEquipmentId}
        equipmentLabel={selectedEquipmentLabel}
        onSuccess={() => loadFuel(selectedEquipmentId)}
      />

      {/* Confirmação de descarte (D-11): leve, NEUTRA (outline) — append-only,
          não apaga nada. Motivo opcional. */}
      <Dialog
        open={!!dismissTarget}
        onOpenChange={(v) => { if (!dismissing && !v) setDismissTarget(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="detection-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Descartar detecção?</DialogTitle>
            <DialogDescription>
              A detecção sai das pendências e não gera lançamento. Esta ação fica
              registrada no ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="detection-dismiss-reason">Motivo (opcional)</Label>
            <Textarea
              id="detection-dismiss-reason"
              data-testid="detection-dismiss-reason"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="opcional"
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDismissTarget(null)}
              disabled={dismissing}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDismissConfirm}
              disabled={dismissing}
              data-testid="detection-dismiss-confirm"
            >
              {dismissing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
